import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { BookOpen, Coins, Loader2, RefreshCw, X } from "lucide-react";
import { useCreateLoanPolicy, useUpdateLoanPolicy } from "@/api/hooks";
import type {
  LoanPolicy,
  LoanPolicyInput,
  Location,
  PatronCategory,
  RecordType,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  POLICY_RULE_SPECS,
  newPolicyFromCategory,
  readDraft,
  readField,
  toDraft,
} from "../../loanRules";
import { RuleGroup, SelectField, SwitchField, UnitField } from "./fields";
import { RECORD_TYPES, RECORD_TYPE_PLURAL_LABELS } from "@/lib/recordTypes";

type NumberField = keyof typeof POLICY_RULE_SPECS;

const ALL = "";

/**
 * Create or edit a loan rule exception. Mounted only while open, so each
 * opening starts from the rule being edited (or the category defaults).
 */
export function PolicyDialog({
  category,
  locations,
  policy,
  onClose,
}: {
  category: PatronCategory;
  locations: Location[];
  /** null creates a new exception */
  policy: LoanPolicy | null;
  onClose: () => void;
}) {
  const start: LoanPolicyInput = policy ?? newPolicyFromCategory(category);
  const [scope, setScope] = useState({
    itemType: start.itemType ?? ALL,
    locationId: start.locationId ?? ALL,
  });
  const [flags, setFlags] = useState({
    loanable: start.loanable,
    renewalsAllowed: start.renewalsAllowed,
    reservationsAllowed: start.reservationsAllowed,
    active: start.active,
  });
  const [numbers, setNumbers] = useState(toDraft<NumberField>(start, POLICY_RULE_SPECS));
  const [maxLoans, setMaxLoans] = useState(start.maxLoans === null ? "" : String(start.maxLoans));
  const [notes, setNotes] = useState(start.notes ?? "");
  const [errors, setErrors] = useState<
    Partial<Record<NumberField | "maxLoans", string | undefined>>
  >({});
  const [serverError, setServerError] = useState<string | null>(null);

  const createPolicy = useCreateLoanPolicy();
  const updatePolicy = useUpdateLoanPolicy();
  const isPending = createPolicy.isPending || updatePolicy.isPending;

  const numberField = (name: NumberField) => ({
    value: numbers[name],
    onChange: (value: string) => {
      setNumbers((current) => ({ ...current, [name]: value }));
      setErrors((current) => ({ ...current, [name]: undefined }));
    },
    error: errors[name],
  });

  const submit = async () => {
    setServerError(null);
    const { values, errors: numberErrors } = readDraft(numbers, POLICY_RULE_SPECS);
    const quota =
      maxLoans.trim() === "" ? null : readField(maxLoans, { kind: "int", min: 0, max: 500 });
    const nextErrors = { ...numberErrors, maxLoans: quota && !quota.ok ? quota.error : undefined };
    setErrors(nextErrors);
    if (!values || (quota && !quota.ok)) return;

    const input: LoanPolicyInput = {
      patronCategoryId: category.id,
      itemType: scope.itemType === ALL ? null : (scope.itemType as RecordType),
      locationId: scope.locationId === ALL ? null : scope.locationId,
      ...values,
      maxLoans: quota ? quota.value : null,
      ...flags,
      notes: notes.trim() === "" ? null : notes.trim(),
    };

    try {
      if (policy) await updatePolicy.mutateAsync({ id: policy.id, input });
      else await createPolicy.mutateAsync(input);
      onClose();
    } catch (err) {
      setServerError(err instanceof Error ? err.message : "L'enregistrement a échoué");
    }
  };

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="animate-fade-in fixed inset-0 z-40 bg-gray-950/40 backdrop-blur-sm" />
        <Dialog.Content className="animate-fade-in fixed inset-y-0 right-0 z-50 flex w-full max-w-2xl flex-col bg-white shadow-2xl dark:bg-gray-900">
          <header className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-5 dark:border-gray-700">
            <div>
              <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
                {policy ? "Modifier l'exception" : "Nouvelle exception"}
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Lecteurs « {category.name} ». Les valeurs de départ sont les règles par défaut :
                changez seulement ce qui diffère.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" aria-label="Fermer">
                <X aria-hidden />
              </Button>
            </Dialog.Close>
          </header>

          <form
            id="loan-policy-form"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
            className="flex-1 space-y-5 overflow-y-auto px-6 py-5"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <SelectField
                label="Type de document"
                value={scope.itemType}
                onChange={(itemType) => {
                  setScope((current) => ({ ...current, itemType }));
                }}
                options={[
                  { value: ALL, label: "Tous les documents" },
                  ...RECORD_TYPES.map((type) => ({
                    value: type,
                    label: RECORD_TYPE_PLURAL_LABELS[type],
                  })),
                ]}
              />
              <SelectField
                label="Site"
                value={scope.locationId}
                onChange={(locationId) => {
                  setScope((current) => ({ ...current, locationId }));
                }}
                options={[
                  { value: ALL, label: "Tous les sites" },
                  ...locations.map((location) => ({ value: location.id, label: location.name })),
                ]}
              />
            </div>

            <div className="space-y-4 rounded-xl border border-gray-200 p-5 dark:border-gray-700">
              <SwitchField
                label="Empruntable"
                description="Désactivé, ces documents restent consultables sur place uniquement."
                checked={flags.loanable}
                onChange={(loanable) => {
                  setFlags((current) => ({ ...current, loanable }));
                }}
              />
              {flags.loanable && (
                <>
                  <SwitchField
                    label="Prolongeable"
                    checked={flags.renewalsAllowed}
                    onChange={(renewalsAllowed) => {
                      setFlags((current) => ({ ...current, renewalsAllowed }));
                    }}
                  />
                  <SwitchField
                    label="Réservable"
                    checked={flags.reservationsAllowed}
                    onChange={(reservationsAllowed) => {
                      setFlags((current) => ({ ...current, reservationsAllowed }));
                    }}
                  />
                </>
              )}
            </div>

            {flags.loanable && (
              <>
                <RuleGroup icon={BookOpen} title="Prêt">
                  <UnitField label="Durée du prêt" unit="jours" {...numberField("loanDays")} />
                  <UnitField
                    label="Documents de ce type"
                    unit="max."
                    placeholder={`${String(category.maxLoans)} (quota global)`}
                    hint="Vide = quota global de la catégorie"
                    value={maxLoans}
                    onChange={(value) => {
                      setMaxLoans(value);
                      setErrors((current) => ({ ...current, maxLoans: undefined }));
                    }}
                    error={errors.maxLoans}
                  />
                </RuleGroup>

                {flags.renewalsAllowed && (
                  <RuleGroup
                    icon={RefreshCw}
                    title="Prolongation"
                    description="La nouvelle date de retour part du jour de la prolongation."
                  >
                    <UnitField
                      label="Prolongations par prêt"
                      unit="max."
                      {...numberField("maxRenewals")}
                    />
                    <UnitField
                      label="Durée de la prolongation"
                      unit="jours"
                      {...numberField("renewalDays")}
                    />
                    <UnitField
                      label="Prolongeable dès"
                      unit="j avant"
                      hint="avant l'échéance"
                      {...numberField("renewalWindowDays")}
                    />
                  </RuleGroup>
                )}

                <RuleGroup icon={Coins} title="Retards">
                  <UnitField
                    label="Amende par jour"
                    unit="€"
                    inputMode="decimal"
                    {...numberField("finePerDayInCents")}
                  />
                  <UnitField
                    label="Plafond par document"
                    unit="€"
                    inputMode="decimal"
                    hint="0 = sans plafond"
                    {...numberField("maxFineInCents")}
                  />
                  <UnitField
                    label="Tolérance avant amende"
                    unit="jours"
                    {...numberField("gracePeriodDays")}
                  />
                </RuleGroup>
              </>
            )}

            <div>
              <label
                htmlFor="loan-policy-notes"
                className="mb-1.5 block text-sm text-gray-700 dark:text-gray-200"
              >
                Note interne
              </label>
              <textarea
                id="loan-policy-notes"
                value={notes}
                onChange={(event) => {
                  setNotes(event.target.value);
                }}
                rows={2}
                maxLength={1000}
                placeholder="Pourquoi cette exception ? (facultatif)"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              />
            </div>

            <SwitchField
              label="Exception active"
              description="Désactivée, elle est conservée mais les règles suivantes s'appliquent."
              checked={flags.active}
              onChange={(active) => {
                setFlags((current) => ({ ...current, active }));
              }}
            />
          </form>

          <footer className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
            {serverError && (
              <p role="alert" className="mr-auto text-sm text-red-700 dark:text-red-400">
                {serverError}
              </p>
            )}
            <Dialog.Close asChild>
              <Button variant="ghost">Annuler</Button>
            </Dialog.Close>
            <Button type="submit" form="loan-policy-form" disabled={isPending}>
              {isPending && <Loader2 className="animate-spin" aria-hidden />}
              {policy ? "Enregistrer" : "Créer l'exception"}
            </Button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
